import React, { ReactNode, ButtonHTMLAttributes } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  icon?: ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  className = '',
  disabled,
  ...props
}) => {
  const { darkMode } = useTheme();

  const baseClasses = 'rounded-xl font-semibold transition-all flex items-center justify-center gap-2';

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  const variantClasses = {
    primary: 'bg-violet-600 hover:bg-violet-700 text-white shadow-lg hover:shadow-xl',
    secondary: darkMode
      ? 'bg-white/10 hover:bg-white/20 text-white border border-transparent'
      : 'bg-white/40 hover:bg-white/60 text-slate-900 border border-slate-300/60',
    danger: darkMode
      ? 'bg-red-500/20 hover:bg-red-500/30 text-red-300'
      : 'bg-red-100 hover:bg-red-200 text-red-700',
    ghost: darkMode
      ? 'bg-transparent hover:bg-white/10 text-white'
      : 'bg-transparent hover:bg-white/40 text-slate-700',
  };

  const disabledClasses = disabled
    ? 'opacity-50 cursor-not-allowed'
    : '';

  return (
    <button
      className={`
        ${baseClasses}
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${fullWidth ? 'w-full' : ''}
        ${disabledClasses}
        ${className}
      `}
      disabled={disabled}
      {...props}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </button>
  );
};

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  tooltip?: string;
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  variant = 'ghost',
  size = 'md',
  tooltip,
  className = '',
  disabled,
  ...props
}) => {
  const { darkMode } = useTheme();

  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3',
  };

  const variantClasses = {
    primary: 'bg-violet-600 hover:bg-violet-700 text-white',
    secondary: darkMode
      ? 'bg-white/10 hover:bg-white/20 text-white border border-transparent'
      : 'bg-white/30 hover:bg-white/50 text-slate-700 border border-slate-300/60',
    danger: darkMode
      ? 'bg-red-500/20 hover:bg-red-500/30 text-red-300'
      : 'bg-red-100 hover:bg-red-200 text-red-700',
    ghost: darkMode
      ? 'bg-transparent hover:bg-white/10 text-white'
      : 'bg-transparent hover:bg-white/40 text-slate-700',
  };

  const disabledClasses = disabled
    ? 'opacity-50 cursor-not-allowed'
    : '';

  return (
    <button
      className={`
        rounded-xl backdrop-blur-md transition-all
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${disabledClasses}
        ${className}
      `}
      title={tooltip}
      disabled={disabled}
      {...props}
    >
      {icon}
    </button>
  );
};

interface AddButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
}

export const AddButton: React.FC<AddButtonProps> = ({
  label = 'Add',
  className = '',
  ...props
}) => {
  return (
    <Button
      variant="primary"
      size="sm"
      className={className}
      {...props}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
      {label}
    </Button>
  );
};
